import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { ProcessingDetailsPanel } from '../components/ProcessingDetailsPanel';
import { DocumentUploadButton } from '../components/DocumentUploadButton';
import { DocumentDialog } from '../components/DocumentDialog';
import { Pagination } from '../components/Pagination';
import { LoadingScreen } from '../components/LoadingScreen';
import { TransactionAPI, AssignmentAPI } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters } from '../hooks/useTransactionFilters';

interface ProcessingSummary {
  total_assigned: number;
  assigned_today: number;
  in_process: number;
  completed: number;
}

const Processing: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('highlight');
  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProcessingSummary>({
    total_assigned: 0,
    assigned_today: 0,
    in_process: 0,
    completed: 0
  });
  // Use a single state object for sorting to avoid state batching issues
  const [sortConfig, setSortConfig] = useState<{
    field: keyof UnifiedTransaction;
    direction: 'asc' | 'desc';
  }>({
    field: 'assigned_at',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [documentDialogRecordId, setDocumentDialogRecordId] = useState<string | null>(null);

  const canViewProcessing = user?.role && ['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin'].includes(user.role);

  // Initialize filters
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredTransactions,
    filterStats
  } = useTransactionFilters(transactions);


  useEffect(() => {
    if (canViewProcessing) {
      loadProcessingTransactions();
    }
  }, [canViewProcessing]);

  // Auto-open details panel when highlight parameter is present
  useEffect(() => {
    if (highlight && transactions.length > 0 && !loading) {
      const highlightedTransaction = transactions.find(t => t.id === highlight);
      if (highlightedTransaction) {
        setSelectedTransaction(highlightedTransaction);
        setShowDetailsPanel(true);
        // Clear the highlight from URL after opening
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [highlight, transactions, loading]);

  const loadProcessingTransactions = async () => {
    try {
      setLoading(true);

      // Table page + true summary tiles (server aggregate over the full
      // dataset). The previous client-side calc summed only the fetched page,
      // and "completed today" relied on a field the API doesn't return.
      const [processingTransactions, summaryStats] = await Promise.all([
        AssignmentAPI.getProcessingTransactions(0, 2000),
        AssignmentAPI.getProcessingSummaryStats(),
      ]);

      setTransactions(processingTransactions);
      setSummary(summaryStats);

    } catch (error: any) {
      console.error('Error loading processing transactions:', error);
      AlertService.error('Failed to load processing transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (transaction: UnifiedTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsPanel(true);
    // Clear any previous decrypted data
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptInPanel = async (transactionId: string, reason: string) => {
    try {
      const data = await TransactionAPI.decryptTransactionData(transactionId, reason);
      setDecryptedData(data);
      setShowEncryptedData(transactionId);
      AlertService.success('Transaction data decrypted successfully');
    } catch (error: any) {
      console.error('Failed to decrypt transaction data:', error);
      AlertService.error(error.message || 'Failed to decrypt transaction data');
    }
  };

  const handleStatusUpdate = async (transactionId: string, newStatus: string) => {
    try {
      setUpdatingStatus(transactionId);
      await AssignmentAPI.updateProcessingStatus(transactionId, newStatus);
      AlertService.success(`Status updated to ${newStatus}`);
      // Reload transactions to get updated data
      await loadProcessingTransactions();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getValidStatusTransitions = (currentStatus: string) => {
    // All possible processing statuses
    const allStatuses = ['ASSIGNED', 'IN_PROCESS', 'COMPLETED', 'CANCELLED', 'DOCUMENTS_REQUIRED', 'CHARGEBACK'];
    
    // For admins, directors, and team leads - allow any status transition
    if (user?.role && ['director', 'admin', 'processor_team_lead', 'floor_manager'].includes(user.role)) {
      return allStatuses.filter(status => status !== currentStatus);
    }
    
    // For regular processor agents - restricted transitions
    const transitions: Record<string, string[]> = {
      'ASSIGNED': ['IN_PROCESS', 'CANCELLED', 'DOCUMENTS_REQUIRED'],
      'IN_PROCESS': ['COMPLETED', 'CANCELLED', 'DOCUMENTS_REQUIRED', 'CHARGEBACK'],
      'DOCUMENTS_REQUIRED': ['IN_PROCESS', 'CANCELLED'],
      'COMPLETED': ['CHARGEBACK'],
      'CANCELLED': [],
      'CHARGEBACK': []
    };
    return transitions[currentStatus] || [];
  };

  const handleSort = (field: keyof UnifiedTransaction) => {
    setSortConfig(prevConfig => {
      let newDirection: 'asc' | 'desc';
      
      if (prevConfig.field === field) {
        // Same field clicked - toggle direction
        newDirection = prevConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        // Different field clicked - default to descending for most fields, ascending for names
        newDirection = field === 'first_name' || field === 'last_name' ? 'asc' : 'desc';
      }
      
      return {
        field: field,
        direction: newDirection
      };
    });
  };

  const sortedAndPaginatedTransactions = React.useMemo(() => {
    // Create a new array to sort (avoid mutating the original)
    const toSort = [...filteredTransactions];
    
    toSort.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      // Handle special cases for computed fields or complex sorting
      if (sortConfig.field === 'processing_status') {
        aVal = a.processing_status || 'PENDING_ASSIGNMENT';
        bVal = b.processing_status || 'PENDING_ASSIGNMENT';
      } else if (sortConfig.field === 'assigned_at' || sortConfig.field === 'created_at' || sortConfig.field === 'updated_at') {
        // For dates, convert to Date objects for proper comparison
        const aDateVal = a[sortConfig.field];
        const bDateVal = b[sortConfig.field];
        
        if (!aDateVal && !bDateVal) return 0;
        if (!aDateVal) return 1;
        if (!bDateVal) return -1;
        
        aVal = new Date(aDateVal);
        bVal = new Date(bDateVal);
      } else {
        aVal = a[sortConfig.field];
        bVal = b[sortConfig.field];
      }
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      else if (aVal > bVal) comparison = 1;
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return toSort.slice(startIndex, endIndex);
  }, [filteredTransactions, sortConfig, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  if (!canViewProcessing) {
    return (
      <DashboardLayout>
        <div className="processing">
          <div className="no-access">
            <h2>Access Denied</h2>
            <p>You don't have permission to view processing transactions.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="processing">
        <div className="page-header">
          <div>
            <h1>Processing Overview</h1>
            <p>Monitor all assigned and in-progress sales processing</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline-primary" onClick={loadProcessingTransactions} disabled={loading}>
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{summary.total_assigned}</h3>
            <p>Total Assigned</p>
          </div>
          <div className="stat-card">
            <h3>{summary.assigned_today}</h3>
            <p>Assigned Today</p>
          </div>
          <div className="stat-card">
            <h3>{summary.in_process}</h3>
            <p>In Process</p>
          </div>
          <div className="stat-card">
            <h3>{summary.completed}</h3>
            <p>Completed</p>
          </div>
        </div>

        {/* Filters */}
        {!loading && transactions.length > 0 && (
          <TransactionFilters
            filters={filters}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            transactions={transactions}
            filterStats={filterStats}
            showVoidRefundFilters={false}
            showProcessingStatusFilter={true}
            showSaleFilters={true}
          />
        )}

        {/* Transactions Table */}
        <LoadingScreen 
          isVisible={loading} 
          message="Loading processing transactions..."
        />
        
        {!loading && (
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('created_by_name')}>
                    Created By 
                    {sortConfig.field === 'created_by_name' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('first_name')}>
                    Customer
                    {sortConfig.field === 'first_name' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('amount')}>
                    Amount
                    {sortConfig.field === 'amount' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>
                    Sale Date
                    {sortConfig.field === 'created_at' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Category</th>
                  <th>State</th>
                  <th className="sortable" onClick={() => handleSort('processing_status')}>
                    Status
                    {sortConfig.field === 'processing_status' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Assigned To</th>
                  <th>Assigned By</th>
                  <th className="sortable" onClick={() => handleSort('assigned_at')}>
                    Assigned At
                    {sortConfig.field === 'assigned_at' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndPaginatedTransactions.map((transaction) => {
                  const currentStatus = transaction.processing_status || 'PENDING_ASSIGNMENT';
                  const customerName = `${transaction.first_name || ''} ${transaction.last_name || ''}`.trim() || 'Unknown';
                  
                  return (
                    <tr key={transaction.id}>
                      <td>{transaction.created_by_name || 'Unknown'}</td>
                      <td>{customerName}</td>
                      <td>${transaction.amount?.toFixed(2) || '0.00'}</td>
                      <td>
                        {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() + ' ' + new Date(transaction.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                      </td>
                      <td>{transaction.category || 'N/A'}</td>
                      <td>
                        {transaction.sale_state || 'N/A'}
                        {transaction.three_ds_status === 'Y' && (
                          <span className="badge-3ds">3DS</span>
                        )}
                        {transaction.three_ds_status === 'Y-3RI' && (
                          <span className="badge-3ds">3RI</span>
                        )}
                      </td>
                      <td>
                        {!user?.view_only && user?.role === 'processor_agent' && (transaction as any).assigned_to_processor === user.id ? (
                          // Dropdown for processor agents assigned to this transaction
                          <select
                            value={currentStatus}
                            onChange={(e) => handleStatusUpdate(transaction.id, e.target.value)}
                            disabled={updatingStatus === transaction.id}
                            className="status-dropdown"
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-primary)',
                              backgroundColor: 'var(--bg-primary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.85rem',
                              width: '160px',
                              minWidth: '160px'
                            }}
                          >
                            <option value={currentStatus}>{currentStatus}</option>
                            {getValidStatusTransitions(currentStatus).map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        ) : !user?.view_only && user?.role && ['processor_team_lead', 'floor_manager', 'director', 'admin'].includes(user.role) ? (
                          // Dropdown for managers/admins (can update any transaction)
                          <select
                            value={currentStatus}
                            onChange={(e) => handleStatusUpdate(transaction.id, e.target.value)}
                            disabled={updatingStatus === transaction.id}
                            className="status-dropdown"
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-primary)',
                              backgroundColor: 'var(--bg-primary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.85rem',
                              width: '160px',
                              minWidth: '160px'
                            }}
                          >
                            <option value={currentStatus}>{currentStatus}</option>
                            {getValidStatusTransitions(currentStatus).map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        ) : (
                          // Static badge for users without permission to update
                          <span className={`status-badge ${currentStatus.toLowerCase().replace('_', '-')}`}>
                            {currentStatus}
                          </span>
                        )}
                        {updatingStatus === transaction.id && (
                          <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            Updating...
                          </span>
                        )}
                      </td>
                      <td>
                        {(transaction as any).assigned_to_processor_name || (transaction as any).assigned_to_processor || 'Unassigned'}
                      </td>
                      <td>
                        {(transaction as any).assigned_by_name || (transaction as any).assigned_by || 'N/A'}
                      </td>
                      <td>
                        {transaction.assigned_at ? new Date(transaction.assigned_at).toLocaleDateString() + ' ' + new Date(transaction.assigned_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <DocumentUploadButton
                            recordId={transaction.id}
                            onClick={() => setDocumentDialogRecordId(transaction.id)}
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleViewDetails(transaction)}
                            title="View Details"
                          >
                            📋 Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <p>No processing transactions found</p>
                {hasActiveFilters && <p>Try adjusting your filters.</p>}
              </div>
            )}
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={filteredTransactions.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        )}

        {/* Transaction Details Panel */}
        {selectedTransaction && showDetailsPanel && (
          <ProcessingDetailsPanel
            transaction={selectedTransaction}
            isOpen={showDetailsPanel}
            onClose={handleCloseDetails}
            decryptedData={showEncryptedData === ((selectedTransaction as any)?.transaction_id || selectedTransaction?.id) ? decryptedData : null}
            onDecryptData={handleDecryptInPanel}
          />
        )}

        {/* Document Dialog */}
        {documentDialogRecordId && (
          <DocumentDialog
            recordId={documentDialogRecordId}
            isOpen={true}
            onClose={() => setDocumentDialogRecordId(null)}
            recordTitle={transactions.find(t => t.id === documentDialogRecordId)?.first_name + ' ' + transactions.find(t => t.id === documentDialogRecordId)?.last_name}
          />
        )}
      </div>

      <style>{`
        .processing {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-header h1 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 2rem;
          font-weight: 600;
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .stat-card h3 {
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 600;
          color: var(--primary);
        }

        .stat-card p {
          margin: 0;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .no-access {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .no-access h2 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-weight: 600;
        }

        .no-access p {
          margin: 0;
          color: var(--text-secondary);
        }

        .transactions-table {
          background: var(--bg-card);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .transactions-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .transactions-table th,
        .transactions-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-primary);
        }

        .transactions-table th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-primary);
          white-space: nowrap;
          cursor: pointer;
          user-select: none;
        }

        .transactions-table th.sortable:hover {
          background: var(--bg-hover);
        }

        .transactions-table td {
          color: var(--text-secondary);
        }

        .transactions-table tbody tr:hover {
          background: var(--bg-hover);
        }

        .transaction-id {
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .transaction-nmi {
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .status-badge.assigned {
          background: var(--info-light);
          color: var(--info);
        }

        .status-badge.in-process {
          background: var(--warning-light);
          color: var(--warning);
        }

        .status-badge.completed {
          background: var(--success-light);
          color: var(--success);
        }

        .status-badge.cancelled,
        .status-badge.pending-assignment {
          background: var(--text-muted-light);
          color: var(--text-muted);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .sort-indicator {
          margin-left: 0.5rem;
          color: var(--primary);
        }

        .no-data {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--text-secondary);
        }

        .no-data p {
          margin: 0 0 0.5rem 0;
        }

        .pagination-container {
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--border-primary);
          background: var(--bg-secondary);
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .transactions-table th,
          .transactions-table td {
            padding: 0.5rem;
            font-size: 0.875rem;
          }

          .action-buttons {
            flex-direction: column;
          }
        }

        .badge-3ds {
          display: inline-block;
          padding: 0.125rem 0.4rem;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 700;
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.3);
          margin-left: 0.25rem;
          vertical-align: middle;
        }
      `}</style>
    </DashboardLayout>
  );
};

export default Processing;